import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Star, LogIn, UserPlus, Shield, ArrowLeft, Loader2, XCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

function getUser() {
  try {
    const u = localStorage.getItem('user_data');
    return u ? JSON.parse(u) : null;
  } catch { return null; }
}

function PayPalCheckoutButton({ plan, user, onSuccess, onError }) {
  return (
    <PayPalButtons
      style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 45 }}
      createOrder={async () => {
        try {
          const res = await axios.post(`${API}/paypal/create-order`, { plan_id: plan.id }, { withCredentials: true });
          return res.data.order_id;
        } catch (err) {
          onError(err.response?.data?.detail || "Failed to create order");
          throw err;
        }
      }}
      onApprove={async (data) => {
        try {
          await axios.post(`${API}/paypal/capture-order/${data.orderID}`, {}, { withCredentials: true });
          onSuccess();
        } catch (err) {
          onError(err.response?.data?.detail || "Payment capture failed");
        }
      }}
      onError={(err) => {
        onError("PayPal encountered an error. Please try again.");
      }}
      onCancel={() => {
        // User cancelled — do nothing
      }}
    />
  );
}

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [user, setUser] = useState(getUser());
  const [mode, setMode] = useState("plans"); // plans | login | register | payment_success | payment_failed
  const [loading, setLoading] = useState(true);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "" });
  const [authError, setAuthError] = useState("");
  const [membershipDisabled, setMembershipDisabled] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        if (settingsRes.data.membership_enabled === false) {
          setMembershipDisabled(true);
          setLoading(false);
          return;
        }
      } catch {}

      try {
        const [plansRes, ppRes] = await Promise.all([
          axios.get(`${API}/membership/plans`),
          axios.get(`${API}/paypal/client-id`)
        ]);
        setPlans(plansRes.data);
        setPaypalClientId(ppRes.data.client_id);
      } catch {}
      setLoading(false);
    };
    init();
  }, []);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    const endpoint = mode === "login" ? "/user/login" : "/user/register";
    const payload = mode === "login" ? { email: authForm.email, password: authForm.password } : authForm;

    try {
      const res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      localStorage.setItem('user_data', JSON.stringify(res.data.user));
      setUser(res.data.user);
      if (pendingPlan) {
        setSelectedPlan(pendingPlan);
        setPendingPlan(null);
      }
      setMode("plans");
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Authentication failed");
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/user/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem('user_data');
    setUser(null);
    setSelectedPlan(null);
  };

  const handlePaymentSuccess = async () => {
    // Refresh user profile
    try {
      const profile = await axios.get(`${API}/user/me`, { withCredentials: true });
      localStorage.setItem('user_data', JSON.stringify(profile.data));
      setUser(profile.data);
    } catch {}
    setSelectedPlan(null);
    setMode("payment_success");
  };

  const handlePaymentError = (msg) => {
    setPaymentError(msg);
    setMode("payment_failed");
  };

  const handleSelectPlan = (plan) => {
    if (!user) {
      setPendingPlan(plan);
      setMode("register");
      return;
    }
    setSelectedPlan(plan);
    setPaymentError("");
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );

  // Membership disabled by admin
  if (membershipDisabled) return (
    <div className="container mx-auto px-4 py-16 max-w-lg text-center" data-testid="membership-disabled">
      <Shield size={48} className="text-[#94A3B8]/40 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Membership Not Available</h1>
      <p className="text-[#94A3B8] mb-6">Membership plans are currently not available. Please check back later.</p>
      <button onClick={() => navigate('/')} className="px-6 py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all">
        Back to Home
      </button>
    </div>
  );

  // Payment verified successfully
  if (mode === "payment_success") return (
    <div className="container mx-auto px-4 py-8 max-w-lg" data-testid="payment-success">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
          <Check size={40} className="text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
        <p className="text-emerald-400 mb-6">Your premium membership is now active. Enjoy unlimited access to all channels!</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate('/')} data-testid="go-home-btn"
            className="px-6 py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all">
            {t("premium.startWatching")}
          </button>
          <button onClick={() => navigate('/account')} data-testid="go-account-btn"
            className="px-6 py-3 bg-[#172033] text-white rounded-xl font-medium hover:bg-[#1e293b] transition-colors border border-white/[0.06]">
            {t("premium.myAccount")}
            View Account
          </button>
        </div>
      </motion.div>
    </div>
  );

  // Payment failed
  if (mode === "payment_failed") return (
    <div className="container mx-auto px-4 py-8 max-w-lg" data-testid="payment-failed">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
          <XCircle size={40} className="text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Payment Not Completed</h2>
        <p className="text-[#94A3B8] mb-6">{paymentError || "Your payment could not be processed. Please try again."}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => { setMode("plans"); setSelectedPlan(null); }} data-testid="retry-btn"
            className="px-6 py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all">
            {t("premium.tryAgain")}
          </button>
          <button onClick={() => navigate('/account')} data-testid="contact-support-btn"
            className="px-6 py-3 bg-[#172033] text-white rounded-xl font-medium hover:bg-[#1e293b] transition-colors border border-white/[0.06]">
            {t("premium.contactSupport")}
            Contact Support
          </button>
        </div>
      </motion.div>
    </div>
  );

  const paypalWrapper = paypalClientId ? (
    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD" }}>
      {selectedPlan && user && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto bg-[#0F172A] rounded-2xl border border-[#FFC928]/30 p-6 mb-8" data-testid="paypal-checkout">
          <div className="text-center mb-4">
            <h3 className="text-lg font-bold text-white">{selectedPlan.name}</h3>
            <div className="flex items-baseline justify-center gap-2 mt-1">
              <span className="text-3xl font-bold text-white">${selectedPlan.price}</span>
              {selectedPlan.original_price > 0 && <span className="text-sm text-[#94A3B8] line-through">${selectedPlan.original_price.toFixed(2)}</span>}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">{selectedPlan.duration_days} days access</p>
          </div>
          <div className="mb-3" data-testid="paypal-buttons">
            <PayPalCheckoutButton
              plan={selectedPlan}
              user={user}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          </div>
          <button onClick={() => setSelectedPlan(null)}
            className="w-full text-center text-xs text-[#94A3B8] hover:text-white transition-colors mt-2">
            Choose a different plan
          </button>
        </motion.div>
      )}
    </PayPalScriptProvider>
  ) : null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="membership-page">
      {/* Header */}
      <div className="text-center mb-12">
        <button onClick={() => navigate(-1)} className="text-[#94A3B8] hover:text-white transition-colors text-sm flex items-center gap-1 mx-auto mb-6">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="w-16 h-16 bg-gradient-to-br from-[#FFC928] to-[#FFD85A] rounded-2xl flex items-center justify-center text-black mx-auto mb-4 shadow-[0_0_20px_rgba(255,201,40,0.3)]">
          <Shield size={32} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins' }}>{t("premium.membershipPlans")}</h1>
        <p className="text-[#94A3B8] text-base max-w-md mx-auto">{t("premium.membershipSubtitle")}</p>
        
        {user && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="text-sm text-[#94A3B8]">{t("premium.loggedInAs")} <strong className="text-white">{user.name || user.email}</strong></span>
            {user.has_membership && <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs font-bold">Premium</span>}
            <button onClick={handleLogout} className="text-xs text-red-400 hover:text-red-300 underline">Logout</button>
          </div>
        )}
      </div>

      {/* Active Membership */}
      {user?.has_membership && (
        <div className="bg-[#FFC928]/10 border border-[#FFC928]/20 rounded-2xl p-6 text-center mb-8">
          <Shield size={32} className="text-blue-400 mx-auto mb-2" />
          <h2 className="text-lg font-bold text-white">You have an active membership</h2>
          <p className="text-[#94A3B8] text-sm mt-1">
            Expires: {user.membership_expires ? new Date(user.membership_expires).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      )}

      {/* Auth forms */}
      {(mode === "login" || mode === "register") && !user && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto bg-[#0F172A] rounded-2xl border border-white/[0.06] p-6 mb-8">
          <h3 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
            {mode === "login" ? <><LogIn size={20} /> {t("account.signInTitle")}</> : <><UserPlus size={20} /> {t("account.createAccountTitle")}</>}
          </h3>
          <form onSubmit={handleAuth} className="space-y-4">
            {authError && <div className="p-3 bg-red-500/10 text-red-400 rounded-xl text-sm border border-red-500/20">{authError}</div>}
            {mode === "register" && (
              <input type="text" placeholder="Your name" data-testid="member-name-input"
                className="w-full px-4 py-3 rounded-xl border border-white/[0.06] bg-[#050814] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20"
                value={authForm.name} onChange={e => setAuthForm({ ...authForm, name: e.target.value })} />
            )}
            <input required type="email" placeholder={t("account.email")} data-testid="member-email-input"
              className="w-full px-4 py-3 rounded-xl border border-white/[0.06] bg-[#050814] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20"
              value={authForm.email} onChange={e => setAuthForm({ ...authForm, email: e.target.value })} />
            <input required type="password" placeholder={t("account.password")} data-testid="member-password-input"
              className="w-full px-4 py-3 rounded-xl border border-white/[0.06] bg-[#050814] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20"
              value={authForm.password} onChange={e => setAuthForm({ ...authForm, password: e.target.value })} />
            <button type="submit" data-testid="member-auth-btn" className="w-full py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all">
              {mode === "login" ? t("account.signInTitle") : t("account.createAccountTitle")}
            </button>
            <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setAuthError(""); }}
              className="w-full text-center text-sm text-[#94A3B8] hover:text-white transition-colors">
              {mode === "login" ? t("premium.dontHaveAccount") : t("premium.alreadyHaveAccount")}
            </button>
            <button type="button" onClick={() => setMode("plans")} className="w-full text-center text-xs text-[#94A3B8]/60 hover:text-[#94A3B8]">{t("premium.cancel")}</button>
          </form>
        </motion.div>
      )}

      {/* PayPal Checkout for Selected Plan */}
      {paypalWrapper}

      {/* Plans Grid */}
      {!selectedPlan && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <motion.div key={plan.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
              className="bg-[#0F172A] rounded-2xl border border-white/[0.06] overflow-hidden hover:border-[#FFC928]/30 transition-all group" data-testid={`plan-${plan.id}`}>
              <div className="p-6">
                {idx === 1 && <div className="flex items-center gap-1 text-xs font-bold text-[#FFC928] mb-3"><Star size={14} className="fill-[#FFC928]" /> {t("premium.bestValue")}</div>}
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-[#94A3B8] text-sm mb-4">{plan.description || "Full access to all channels"}</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-bold text-white">${plan.price}</span>
                  {plan.original_price > 0 && <span className="text-lg text-[#94A3B8] line-through">${plan.original_price.toFixed(2)}</span>}
                </div>
                <span className="text-sm text-[#94A3B8]">/ {plan.duration_days} {t("premium.daysAccess", { count: plan.duration_days }).replace(plan.duration_days + " ", "")}</span>
                {plan.original_price > 0 && (
                  <div className="mt-2 inline-block px-2.5 py-0.5 bg-green-500/10 text-green-400 rounded-lg text-xs font-bold">
                    SAVE {Math.round((1 - plan.price / plan.original_price) * 100)}%
                  </div>
                )}
              </div>
              <div className="px-6 pb-6">
                <ul className="space-y-2 mb-6">
                  {["Unlimited channel access", "HD & 4K streams", "All devices supported", "No ads"].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#94A3B8]"><Check size={14} className="text-emerald-400 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan)}
                  data-testid={`checkout-${plan.id}`}
                  className="w-full py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all flex items-center justify-center gap-2"
                >
                  {user?.has_membership ? t("premium.switchPlan") : t("premium.subscribeNow")}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {plans.length === 0 && (
        <p className="text-center text-[#94A3B8] py-12">{t("premium.notAvailableSubtitle")}</p>
      )}
    </div>
  );
}
